import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLangContext } from 'components/shared/Lang';
import Link from 'components/shared/elements/Link';
import { useAuthContext } from 'components/shared/Auth';
import { useRouter } from 'next/router';
import {
  AudioOutlined,
  CustomerServiceOutlined,
  FileTextOutlined,
  LinkOutlined,
  LockOutlined,
  PlusCircleOutlined,
  ReloadOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import styles from 'components/ant/css/stylesIndex.module.css';
import { Button, Empty, Image, Input, List, Modal, Segmented, Spin, message } from 'antd';
import InviteLinkModal from 'components/views/chat/InviteLinkModal';

const getLastMessagePreview = (lastMessage, fallback = '') => {
  if (lastMessage?.type === 'call' || lastMessage?.call) return 'Звонок';
  if (lastMessage?.sticker) return 'Стикер';
  if (Array.isArray(lastMessage?.images) && lastMessage.images.some((item) => item?.is_circle)) return 'Кружок';
  if (Array.isArray(lastMessage?.images) && lastMessage.images.some((item) => item?.mime_type === 'video')) return 'Видео';
  if (Array.isArray(lastMessage?.images) && lastMessage.images.some((item) => item?.mime_type === 'audio')) return 'Аудио';
  if (Array.isArray(lastMessage?.images) && lastMessage.images.length > 0) return 'Фото';
  if (Array.isArray(lastMessage?.files) && lastMessage.files.length > 0) return 'Файл';

  const value = lastMessage?.comment ?? lastMessage?.message ?? lastMessage ?? fallback;
  const candidates = [lastMessage?.game, value];

  if (candidates.some((candidate) => candidate && typeof candidate === 'object' && candidate.type === 'game')) {
    return null;
  }

  if (typeof value !== 'string') return '';

  try {
    const parsed = JSON.parse(value);
    if (parsed?.type === 'game' || parsed?.message?.type === 'game') return 'Игра';
  } catch {
    // Keep ordinary text unchanged.
  }

  return value;
};

const getRoomSortValue = (room) => {
  const timestamp =
    room?.last_message_at ||
    room?.last_message?.created_at ||
    room?.last_message?.updated_at ||
    room?.updated_at ||
    room?.created_at;
  const parsed = Date.parse(String(timestamp || ''));
  return Number.isFinite(parsed) ? parsed : Number(room?.last_message_id) || 0;
};

const formatRoomTime = (room) => {
  const timestamp =
    room?.last_message_at ||
    room?.last_message?.created_at ||
    room?.last_message?.updated_at;
  const date = new Date(timestamp || '');
  if (Number.isNaN(date.getTime())) return '';

  const now = new Date();
  if (date.toDateString() === now.toDateString()) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return 'вчера';
  return date.toLocaleDateString([], { day: '2-digit', month: '2-digit' });
};

const getRoomIcon = (room) => {
  if (room?.type === 'group') return <TeamOutlined />;
  if (room?.type === 'channel') return <AudioOutlined />;
  if (room?.type === 'support') return <CustomerServiceOutlined />;
  if (room?.type === 'listing') return <FileTextOutlined />;
  return <UserOutlined />;
};

const isClosedGroup = (room) =>
  room?.type === 'group' &&
  (room?.is_open === false || room?.settings?.is_open === false || room?.chat_room?.settings?.is_open === false);

export default function Chats() {
  const { t } = useLangContext();
  const router = useRouter();
  const { userData } = useAuthContext();

  const [chatItems, setChatItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [chatType, setChatType] = useState('private');
  const [counter, setCounter] = useState({ private: 0, listing: 0 });

  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [usersFound, setUsersFound] = useState([]);
  const [creatingChatWithId, setCreatingChatWithId] = useState(null);
  const [inviteTarget, setInviteTarget] = useState(null);

  const getChats = useCallback((isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    fetch(`/api/user/chat-rooms`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then((response) => response.json())
      .then((data) => {
        const rooms = data?.data ?? [];
        const counts = { private: 0, listing: 0 };

        rooms.forEach((item) => {
          if (item?.type === 'private' || item?.type === 'group' || item?.type === 'channel') counts.private += 1;
          if (item?.type === 'listing') counts.listing += 1;
        });

        setCounter(counts);
        setChatItems([...rooms].sort((a, b) => getRoomSortValue(b) - getRoomSortValue(a)));
      })
      .catch((error) => {
        console.error('getChatsError', error);
      })
      .finally(() => {
        setLoading(false);
        if (isRefresh) setRefreshing(false);
      });
  }, []);

  useEffect(() => {
    getChats();
  }, [getChats]);

  useEffect(() => {
    if (!searchModalOpen) return;

    const q = searchTerm.trim();
    if (q.length < 2) {
      setUsersFound([]);
      setSearchLoading(false);
      return;
    }

    const timer = setTimeout(() => {
      setSearchLoading(true);
      fetch(`/api/search/users?q=${encodeURIComponent(q)}`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      })
        .then((response) => response.json())
        .then((data) => {
          const items = Array.isArray(data) ? data : data?.data ?? [];
          const filteredItems = items.filter((user) => user?.id && user?.id !== userData?.id);
          setUsersFound(filteredItems);
        })
        .catch(() => {
          setUsersFound([]);
        })
        .finally(() => {
          setSearchLoading(false);
        });
    }, 350);

    return () => clearTimeout(timer);
  }, [searchTerm, searchModalOpen, userData?.id]);

  const privateChats = useMemo(
    () => chatItems.filter((item) => item?.type === 'private' || item?.type === 'group' || item?.type === 'channel'),
    [chatItems],
  );
  const listingChats = useMemo(() => chatItems.filter((item) => item?.type === 'listing'), [chatItems]);
  const filteredChats = chatType === 'private' || chatType === 'group' ? privateChats : listingChats;

  const openPrivateChat = (user) => {
    const userUuid = user?.user_id ?? user?.uuid ?? user?.user_uuid;
    if (!userUuid) return;
    setCreatingChatWithId(userUuid);
    fetch('/api/chat-rooms/private', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_uuid: String(userUuid) }),
    })
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data?.message || 'Unable to create chat');
        }
        return data;
      })
      .then((data) => {
        setSearchModalOpen(false);
        setSearchTerm('');
        setUsersFound([]);
        getChats();
        router.push(`/chat/users/${data?.data?.uuid}`);
      })
      .catch(() => {
        message.error(t('error') || 'Unable to create chat');
      })
      .finally(() => {
        setCreatingChatWithId(null);
      });
  };

  const renderChatItem = (item) => {
    const isPrivate = item?.type === 'private' || item?.type === 'group' || item?.type === 'channel';
    const listing = item?.listing;
    const avatar = item?.icon;
    const isDirectChat = item?.type === 'private';

    const chatLink = isPrivate
      ? `/chat/users/${item?.uuid ?? item?.user?.id ?? item?.user?.uuid}`
      : `/listings/${listing?.slug ?? listing?.id}`;

    const title = item?.name ?? listing?.title ?? item?.user?.name ?? (item?.type === 'support' ? t('ZotZon Support') : '-');
    const lastMessage = getLastMessagePreview(item?.last_message, item?.message);
    const messagesCount = item?.messages_count ?? item?.msgs ?? 0;
    const unreadCount = Number(item?.unread_messages_count || 0);
    const closedGroup = isClosedGroup(item);
    const lastMessageAuthor = !isDirectChat ? String(item?.last_message?.user_name || '').trim() : '';
    const lastMessageTime = formatRoomTime(item);

    return (
      <div className={styles.chatsDiv} key={item?.id ?? item?.uuid}>
        <Link href={chatLink} style={{ position: 'relative', display: 'inline-flex', width: 36, height: 40, marginRight: 16 }}>
          {avatar ? (
            <Image className={styles.chatsAvatar} preview={false} src={avatar} alt="chat avatar" />
          ) : (
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 18,
                marginTop: 4,
                background: '#f0f0f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#8c8c8c',
              }}
            >
              {getRoomIcon(item)}
            </div>
          )}
          {closedGroup && (
            <span
              aria-label={t('Private group')}
              style={{
                position: 'absolute',
                right: -6,
                bottom: -3,
                width: 16,
                height: 16,
                border: '2px solid #fff',
                borderRadius: '50%',
                background: '#59636e',
                color: '#fff',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 9,
                lineHeight: 1,
              }}
            >
              <LockOutlined />
            </span>
          )}
        </Link>

        <div className={styles.chatsDiv1}>
          <div className={styles.chatsDiv1}>
            <div className={styles.chatsDiv2} style={{ display: 'flex', alignItems: 'center', gap: 8, paddingRight: 10 }}>
              <Link href={chatLink} style={{ minWidth: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {title}
              </Link>
              <span className={styles.chatsDiv3} style={{ position: 'static', flexShrink: 0 }}>
                {lastMessageTime}
              </span>
            </div>
            <div className={styles.chatsDiv4}>
              {!!lastMessage && (
                <div className={styles.chatsDiv5} style={{ display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden', paddingRight: 28 }}>
                  {lastMessageAuthor && <span style={{ color: '#999' }}>{lastMessageAuthor}: </span>}
                  {lastMessage}
                </div>
              )}
              {!isDirectChat && !lastMessage && item?.last_message?.user_name && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, paddingTop: 2 }}>
                  {item?.last_message?.user_avatar ? (
                    <Image
                      width={12}
                      height={12}
                      preview={false}
                      style={{ borderRadius: 6 }}
                      src={item.last_message.user_avatar}
                      alt="last message author avatar"
                    />
                  ) : (
                    <UserOutlined style={{ fontSize: 12, color: '#aaa' }} />
                  )}
                  <span className={styles.chatsSpan}>{item.last_message.user_name}</span>
                </div>
              )}
              {unreadCount > 0 && (
                <span
                  style={{
                    position: 'absolute',
                    right: 10,
                    bottom: 2,
                    minWidth: 20,
                    height: 20,
                    padding: '0 6px',
                    borderRadius: 10,
                    background: '#1677ff',
                    color: '#fff',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 11,
                    fontWeight: 600,
                  }}
                >
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.chatsDiv7}>
      {loading && <Spin style={{ position: 'absolute', top: '10px', left: '20px', zIndex: '20' }} />}

      {userData && (
        <>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
            <Segmented
              value={chatType}
              onChange={setChatType}
              options={[
                { label: `Private ${counter.private > 0 ? `(${counter.private})` : ''}`, value: 'private' },
                { label: `Listing ${counter.listing > 0 ? `(${counter.listing})` : ''}`, value: 'listing' },
              ]}
            />

            <Button icon={<ReloadOutlined />} loading={refreshing} onClick={() => getChats(true)}>
              Refresh
            </Button>

            {chatType === 'private' && (
              <Button type="text" icon={<PlusCircleOutlined />} onClick={() => setSearchModalOpen(true)} />
            )}
            {userData?.id && (
              <Button
                icon={<LinkOutlined />}
                onClick={() => {
                  setInviteTarget({ targetType: 'user', target: userData.user_id || userData.id, title: userData.name, avatar: userData.avatar })
                }}
              >
                {t('Пригласить')}
              </Button>
            )}
          </div>

          {!loading && filteredChats.length === 0 && (
            <Empty description={chatType === 'private' ? t('privateChats') || 'No private chats' : t('listingsChats') || 'No listing chats'} />
          )}
          {filteredChats.map((item) => renderChatItem(item))}

          <Modal
            title={t('privateChats') || 'Private chat'}
            open={searchModalOpen}
            onCancel={() => {
              setSearchModalOpen(false);
              setSearchTerm('');
              setUsersFound([]);
            }}
            footer={null}
          >
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('search') || 'Search user'}
            />

            <List
              loading={searchLoading}
              style={{ marginTop: 12, maxHeight: 420, overflow: 'auto' }}
              dataSource={usersFound}
              locale={{ emptyText: searchTerm.trim().length < 2 ? 'Min 2 chars' : 'No users found' }}
              renderItem={(item) => (
                <List.Item
                  key={item.user_id ?? item.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => openPrivateChat(item)}
                  actions={[
                    <Button
                      key={`open-${item.user_id ?? item.id}`}
                      loading={creatingChatWithId === (item.user_id ?? item.id)}
                      size="small"
                    >
                      Open
                    </Button>,
                  ]}
                >
                  <List.Item.Meta
                    avatar={
                      item?.avatar ? (
                        <Image
                          width={36}
                          height={36}
                          preview={false}
                          style={{ borderRadius: 18 }}
                          src={item.avatar}
                          alt="user avatar"
                        />
                      ) : (
                        <div
                          style={{
                            width: 36,
                            height: 36,
                            borderRadius: 18,
                            background: '#f0f0f0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#8c8c8c',
                          }}
                        >
                          <UserOutlined />
                        </div>
                      )
                    }
                    title={item?.name || item?.nickname || item?.phone}
                    description={item?.nickname ? `@${item.nickname}` : item?.phone}
                  />
                </List.Item>
              )}
            />
          </Modal>
          <InviteLinkModal
            open={Boolean(inviteTarget)}
            onClose={() => setInviteTarget(null)}
            targetType={inviteTarget?.targetType}
            target={inviteTarget?.target}
            title={inviteTarget?.title}
            targetAvatar={inviteTarget?.avatar}
            t={t}
          />
        </>
      )}
    </div>
  );
}
